import type { ISize } from '@pressreader/geometry';
import { ScreenSizeType } from '@pressreader/ui-types';
import { device, getScreenSize } from '@pressreader/utils';

const viewportSizeToType = [
    [1200, ScreenSizeType.XLarge],
    [992, ScreenSizeType.Large],
    [768, ScreenSizeType.Medium],
    [480, ScreenSizeType.Small],
    [0, ScreenSizeType.XSmall],
] as const;

/**
 * Get the view size type based on the given size.
 *
 * @param size - The size of the viewport (e.g. width).
 * @returns - The corresponding screen size type.
 */
export function getSizeType(size: number): ScreenSizeType {
    for (const item of viewportSizeToType) {
        if (size >= item[0]) {
            return item[1];
        }
    }

    return ScreenSizeType.XSmall;
}

/**
 * Determine if the given size corresponds to a compact screen (medium or smaller).
 *
 * @param size - The size of the viewport (e.g. width).
 * @returns - Whether the given size corresponds to a compact screen (medium or smaller).
 */
export function getIsCompactScreen(screenType: ScreenSizeType): boolean {
    return screenType <= ScreenSizeType.Medium;
}

/**
 * Get the viewport size type based on the given size and device type.
 * @param size - The size of the viewport.
 * @param isMobile - Whether the device is a mobile device.
 * @returns - The corresponding screen size type.
 */
export function getViewportSizeType(size: ISize, isMobile = false): ScreenSizeType {
    let width = size.width;
    if (isMobile) {
        // handle landscape orientation for mobile devices
        width = Math.min(size.width, size.height);
    }

    return getSizeType(width);
}

export function getScreenSizeType() {
    const size = getScreenSize();
    return getViewportSizeType(size, device.mobile);
}
